import { ApiResponse } from '@nf/types/api';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useApiBackendInfo } from '@/hooks/query/use-api-backend-info';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { ApiBackendInfo } from '@/hooks/query/athena/types';
import { WcPromotionSetting } from './types';
import { WC_PROMOTION_SETTING_CACHE_MS } from './constants';

const fetchWcPromotionSetting = async (apiBackendInfo: ApiBackendInfo): Promise<ApiResponse<WcPromotionSetting>> => {
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/WcPromotion/GetSetting`)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

// 獲取獎池的設定, 使用在整個活動專區中
export const useWcPromotionSetting = () => {
	const { enableWcPromotionLuckySpin, enableWcPromotionWeeklyLottery, enableWcPromotionAfterParty } = useLicSettings();
	const apiBackendInfo = useApiBackendInfo();
	const isWcPromotionEnabled = enableWcPromotionLuckySpin || enableWcPromotionWeeklyLottery || enableWcPromotionAfterParty;

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [
			queryKeys.WC_PROMOTION_SETTING,
			apiBackendInfo.url,
			apiBackendInfo.token
		],
		queryFn: () => fetchWcPromotionSetting(apiBackendInfo),
		staleTime: WC_PROMOTION_SETTING_CACHE_MS,
		cacheTime: WC_PROMOTION_SETTING_CACHE_MS,
		enabled: isWcPromotionEnabled && apiBackendInfo.isReady
	});

	const isWcPromotionSupported = (sportId: number, leagueGroupId: number) => {
		const setting = data?.Data;
		return Boolean(
			isWcPromotionEnabled &&
			setting?.SportId === sportId &&
			setting?.LeagueGroupId?.includes(leagueGroupId)
		);
	};

	const isWcPromotionSportIdSupported = (sportId: number) => {
		return Boolean(
			isWcPromotionEnabled &&
			data?.Data?.SportId === sportId
		);
	};

	const convertAmountByCurrency = (amount: number, userCurrencyId: number) => {
		const currId = userCurrencyId ?? 2; // default to RM (馬幣)
		const currencySetting = data?.Data?.CurrencySettings?.find(setting => setting.CurrencyId === currId);
		return amount / (currencySetting?.ActualRate ?? 1);
	};

	return {
		data: data?.Data,
		isLoading,
		isError,
		error,
		refetch,
		isWcPromotionSupported,
		isWcPromotionSportIdSupported,
		convertAmountByCurrency,
	};
};
